import { Check, CircleAlert, Pause, Play, RotateCcw, ShieldCheck, Snail, XCircle } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

type CandidateLabel = 'A' | 'B' | 'C'
type RejectReason = 'schwa_added' | 'wrong_vowel' | 'wrong_phoneme' | 'letter_name' | 'accent_inconsistent' | 'too_long' | 'too_short' | 'too_quiet' | 'clipped' | 'unnatural' | 'other'

interface CandidateRecord {
  candidateId: string
  assetId: string
  phonemeId: string
  ipa: string
  canonicalExample: string
  candidateLabel: CandidateLabel
  sourceWord: string
  sourceRange: { startMs: number; endMs: number }
  durationMs: number
  generationMethod: string
  filePath: string
  contextPath: string
  qaStatus: 'needs_review'
  signal: { warnings: string[] }
}

interface CandidateManifest {
  generatedAt: string
  records: CandidateRecord[]
}

interface CandidateReview {
  passedChecks: string[]
  rejectedReason?: RejectReason
}

interface PhonemeReview {
  promotedCandidate?: CandidateLabel
  candidates: Record<string, CandidateReview>
}

type QaState = Record<string, PhonemeReview>

const storageKey = 'phonics-garden.stage1-golden-master-qa.v1'
const phonemeOrder = ['phoneme_s', 'phoneme_short_a_ae', 'phoneme_t', 'phoneme_p', 'phoneme_short_i_ih', 'phoneme_n']
const rejectReasons: RejectReason[] = ['schwa_added', 'wrong_vowel', 'wrong_phoneme', 'letter_name', 'accent_inconsistent', 'too_long', 'too_short', 'too_quiet', 'clipped', 'unnatural', 'other']

const checklistFor = (phonemeId: string) => {
  if (phonemeId === 'phoneme_t' || phonemeId === 'phoneme_p') return [
    '没有明显 /ə/',
    '没有夹带后续 vowel',
    'burst 完整',
    '没有因过短而几乎听不到',
    '没有包含 source word 的其他音',
  ]
  if (phonemeId === 'phoneme_short_a_ae' || phonemeId === 'phoneme_short_i_ih') return [
    '元音片段纯净',
    '没有截入前一个 consonant',
    '没有截入后一个 consonant',
    'vowel quality 是稳定的 General American',
    '没有明显不适合作为教学标准音的语境变体',
  ]
  return [
    '可以清晰独立听辨',
    '没有明显后续 schwa',
    phonemeId === 'phoneme_n' ? '/n/ 没有混入后续 vowel' : '没有混入 source word 的后续 vowel',
    'sustain 适合 phonics teaching',
  ]
}

const readQa = (): QaState => {
  try { return JSON.parse(localStorage.getItem(storageKey) ?? '{}') as QaState }
  catch { return {} }
}

export function Stage1AudioQa() {
  const [manifest, setManifest] = useState<CandidateManifest | null>(null)
  const [loadError, setLoadError] = useState('')
  const [qa, setQa] = useState<QaState>(readQa)
  const [rejectSelections, setRejectSelections] = useState<Record<string, RejectReason>>({})
  const activeAudio = useRef<HTMLAudioElement | null>(null)
  const playSequence = useRef(0)

  useEffect(() => {
    let current = true
    void fetch('/audio/stage1-qa-manifest.json')
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.json() as Promise<CandidateManifest>
      })
      .then((data) => { if (current) setManifest(data) })
      .catch((error: unknown) => { if (current) setLoadError(error instanceof Error ? error.message : String(error)) })
    return () => { current = false; activeAudio.current?.pause() }
  }, [])

  const grouped = useMemo(() => {
    const groups = new Map<string, CandidateRecord[]>()
    manifest?.records.forEach((record) => groups.set(record.phonemeId, [...(groups.get(record.phonemeId) ?? []), record]))
    groups.forEach((records) => records.sort((left, right) => left.candidateLabel.localeCompare(right.candidateLabel)))
    return groups
  }, [manifest])

  const saveQa = (next: QaState) => {
    setQa(next)
    localStorage.setItem(storageKey, JSON.stringify(next))
  }

  const stop = () => {
    playSequence.current += 1
    if (activeAudio.current) {
      activeAudio.current.pause()
      activeAudio.current.currentTime = 0
      activeAudio.current = null
    }
  }

  const playPath = (path: string, rate = 1, sequence = ++playSequence.current) => new Promise<void>((resolve) => {
    if (activeAudio.current) activeAudio.current.pause()
    const audio = new Audio(path)
    audio.playbackRate = rate
    audio.preservesPitch = true
    const pitchSafe = audio as HTMLAudioElement & { webkitPreservesPitch?: boolean }
    if ('webkitPreservesPitch' in pitchSafe) pitchSafe.webkitPreservesPitch = true
    activeAudio.current = audio
    const finish = () => { if (playSequence.current === sequence) activeAudio.current = null; resolve() }
    audio.addEventListener('ended', finish, { once: true })
    audio.addEventListener('error', finish, { once: true })
    void audio.play().catch(finish)
  })

  const playNormal = (record: CandidateRecord, rate = 1) => {
    stop()
    void playPath(record.filePath, rate)
  }

  const playContext = async (record: CandidateRecord) => {
    stop()
    const sequence = ++playSequence.current
    await playPath(record.contextPath, 1, sequence)
    if (playSequence.current !== sequence) return
    await new Promise((resolve) => window.setTimeout(resolve, 500))
    if (playSequence.current !== sequence) return
    await playPath(record.filePath, 1, sequence)
  }

  const candidateReview = (phonemeId: string, candidateId: string): CandidateReview => qa[phonemeId]?.candidates[candidateId] ?? { passedChecks: [] }

  const toggleCheck = (record: CandidateRecord, check: string) => {
    const phonemeReview = qa[record.phonemeId] ?? { candidates: {} }
    const current = phonemeReview.candidates[record.candidateId] ?? { passedChecks: [] }
    const passedChecks = current.passedChecks.includes(check)
      ? current.passedChecks.filter((item) => item !== check)
      : [...current.passedChecks, check]
    saveQa({
      ...qa,
      [record.phonemeId]: {
        ...phonemeReview,
        candidates: { ...phonemeReview.candidates, [record.candidateId]: { ...current, passedChecks } },
      },
    })
  }

  const reject = (record: CandidateRecord) => {
    const phonemeReview = qa[record.phonemeId] ?? { candidates: {} }
    const current = phonemeReview.candidates[record.candidateId] ?? { passedChecks: [] }
    const promotedCandidate = phonemeReview.promotedCandidate === record.candidateLabel ? undefined : phonemeReview.promotedCandidate
    saveQa({
      ...qa,
      [record.phonemeId]: {
        promotedCandidate,
        candidates: {
          ...phonemeReview.candidates,
          [record.candidateId]: { ...current, rejectedReason: rejectSelections[record.candidateId] ?? 'other' },
        },
      },
    })
  }

  const restore = (record: CandidateRecord) => {
    const phonemeReview = qa[record.phonemeId] ?? { candidates: {} }
    const current = phonemeReview.candidates[record.candidateId] ?? { passedChecks: [] }
    saveQa({
      ...qa,
      [record.phonemeId]: {
        ...phonemeReview,
        candidates: { ...phonemeReview.candidates, [record.candidateId]: { passedChecks: current.passedChecks } },
      },
    })
  }

  const promote = (record: CandidateRecord) => {
    if (!window.confirm(`确认已人工试听，并将 Candidate ${record.candidateLabel}（${record.sourceWord}）晋升为 ${record.ipa} Golden Master？`)) return
    const phonemeReview = qa[record.phonemeId] ?? { candidates: {} }
    const current = phonemeReview.candidates[record.candidateId] ?? { passedChecks: [] }
    saveQa({
      ...qa,
      [record.phonemeId]: {
        promotedCandidate: record.candidateLabel,
        candidates: { ...phonemeReview.candidates, [record.candidateId]: { passedChecks: current.passedChecks } },
      },
    })
  }

  if (loadError) return <div className="rounded-3xl bg-rose-50 p-6 font-bold text-rose-800">Stage 1 manifest 加载失败：{loadError}</div>
  if (!manifest) return <div className="rounded-3xl bg-white p-6 font-bold text-garden-700 shadow-soft">正在载入 Stage 1 candidates…</div>

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border-2 border-garden-200 bg-white p-5 shadow-soft">
        <div className="flex flex-wrap items-center gap-3"><ShieldCheck className="h-7 w-7 text-garden-700" /><h2 className="text-2xl font-black">Stage 1 Golden Master QA</h2><span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-black text-amber-800">人工 promotion 前全部 needs_review</span></div>
        <p className="mt-2 font-bold text-garden-700">只检查 /s/ · /æ/ · /t/ · /p/ · /ɪ/ · /n/。慢放仅供 QA，使用保调速播放；信号分析不会自动 Verify。</p>
      </div>

      {phonemeOrder.map((phonemeId) => {
        const records = grouped.get(phonemeId) ?? []
        if (!records.length) return null
        const first = records[0]!
        const promoted = qa[phonemeId]?.promotedCandidate
        const highRisk = phonemeId === 'phoneme_t' || phonemeId === 'phoneme_p'
        return <section key={phonemeId} className={`rounded-[2rem] border-2 bg-white p-5 shadow-soft ${highRisk ? 'border-rose-200' : 'border-garden-100'}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><p className="font-mono text-xs font-black text-garden-600">{phonemeId}</p><h3 className="mt-1 text-3xl font-black">{first.ipa} <span className="text-base text-garden-600">canonical example: {first.canonicalExample}</span></h3></div>
            <div className="flex flex-wrap gap-2">{highRisk && <span className="rounded-full bg-rose-100 px-3 py-2 text-xs font-black text-rose-800">HIGH SCHWA / COARTICULATION RISK</span>}<span className={`rounded-full px-3 py-2 text-xs font-black ${promoted ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{promoted ? `verified · Golden Master ${promoted}` : 'needs_review'}</span></div>
          </div>
          <div className="mt-5 grid gap-4 xl:grid-cols-3">{records.map((record) => {
            const review = candidateReview(phonemeId, record.candidateId)
            const checks = checklistFor(phonemeId)
            const allPassed = checks.every((check) => review.passedChecks.includes(check))
            const rejected = Boolean(review.rejectedReason)
            const isPromoted = promoted === record.candidateLabel
            return <article key={record.candidateId} className={`rounded-3xl border-2 p-4 ${isPromoted ? 'border-emerald-300 bg-emerald-50/60' : rejected ? 'border-rose-200 bg-rose-50/40' : 'border-garden-100 bg-garden-50/40'}`}>
              <div className="flex items-center justify-between gap-3"><h4 className="text-xl font-black">Candidate {record.candidateLabel}</h4><span className={`rounded-full px-3 py-1 text-xs font-black ${isPromoted ? 'bg-emerald-100 text-emerald-800' : rejected ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>{isPromoted ? 'verified' : rejected ? `rejected · ${review.rejectedReason}` : record.qaStatus}</span></div>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm"><dt className="font-black">source</dt><dd className="font-bold">{record.sourceWord}</dd><dt className="font-black">range</dt><dd className="font-mono font-bold">{record.sourceRange.startMs}–{record.sourceRange.endMs} ms</dd><dt className="font-black">duration</dt><dd className="font-bold">{record.durationMs} ms</dd><dt className="font-black">method</dt><dd className="font-bold text-garden-700">{record.generationMethod}</dd></dl>
              {record.signal.warnings.length > 0 && <p className="mt-3 rounded-2xl bg-amber-100 px-3 py-2 text-xs font-black text-amber-900">Signal: {record.signal.warnings.join(', ')}</p>}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button onClick={() => playNormal(record)} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-garden-800 px-3 text-sm font-black text-white"><Play className="h-4 w-4" />Normal</button>
                <button onClick={() => playNormal(record)} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-garden-100 px-3 text-sm font-black text-garden-800"><RotateCcw className="h-4 w-4" />Replay</button>
                <button onClick={() => playNormal(record, 0.65)} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-sky-100 px-3 text-sm font-black text-sky-900"><Snail className="h-4 w-4" />Slow QA</button>
                <button onClick={() => void playContext(record)} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-violet-100 px-3 text-sm font-black text-violet-900"><Pause className="h-4 w-4" />Context</button>
              </div>
              <div className="mt-4 space-y-2 rounded-2xl bg-white/80 p-3">{checks.map((check) => <label key={check} className="flex cursor-pointer items-start gap-2 text-sm font-bold text-garden-800"><input type="checkbox" checked={review.passedChecks.includes(check)} onChange={() => toggleCheck(record, check)} className="mt-0.5 h-5 w-5 accent-emerald-600" /><span>{check}</span></label>)}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => promote(record)} disabled={!allPassed || rejected} className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-35"><Check className="h-4 w-4" />Promote {record.candidateLabel}</button>
                {rejected ? <button onClick={() => restore(record)} className="min-h-12 rounded-2xl bg-amber-100 px-3 text-sm font-black text-amber-900">恢复复核</button> : <><select value={rejectSelections[record.candidateId] ?? 'other'} onChange={(event) => setRejectSelections((value) => ({ ...value, [record.candidateId]: event.target.value as RejectReason }))} className="min-h-12 max-w-36 rounded-2xl bg-rose-50 px-2 text-xs font-bold">{rejectReasons.map((reason) => <option key={reason}>{reason}</option>)}</select><button onClick={() => reject(record)} className="grid h-12 w-12 place-items-center rounded-2xl bg-rose-100 text-rose-800" title="Reject candidate"><XCircle className="h-5 w-5" /></button></>}
              </div>
              {!allPassed && !rejected && <p className="mt-2 flex items-center gap-1 text-xs font-bold text-amber-800"><CircleAlert className="h-4 w-4" />完成全部听觉检查后才能人工 Promote。</p>}
            </article>
          })}</div>
        </section>
      })}
      <button onClick={stop} className="fixed bottom-24 right-5 z-30 grid h-14 w-14 place-items-center rounded-full bg-garden-900 text-white shadow-soft" aria-label="停止播放"><Pause /></button>
    </div>
  )
}
